/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {PRIMARY_OUTLET, ParamMap, convertToParamMap} from './shared';
import {forEach, shallowEqual} from './utils/collection';
/**
 * @return {?}
 */
export function createEmptyUrlTree() {
  return new UrlTree(new UrlSegmentGroup([], {}), {}, null);
}
/**
 * @param {?} container
 * @param {?} containee
 * @param {?} exact
 * @return {?}
 */
export function containsTree(container: UrlTree, containee: UrlTree, exact: boolean): boolean {
  if (exact) {
    return equalQueryParams(container.queryParams, containee.queryParams) &&
        equalSegmentGroups(container.root, containee.root);
  }

  return containsQueryParams(container.queryParams, containee.queryParams) &&
      containsSegmentGroup(container.root, containee.root);
}
/**
 * @param {?} container
 * @param {?} containee
 * @return {?}
 */
function equalQueryParams(
    container: {[k: string]: string}, containee: {[k: string]: string}): boolean {
  return shallowEqual(container, containee);
}
/**
 * @param {?} container
 * @param {?} containee
 * @return {?}
 */
function equalSegmentGroups(container: UrlSegmentGroup, containee: UrlSegmentGroup): boolean {
  if (!equalPath(container.segments, containee.segments)) return false;
  if (container.numberOfChildren !== containee.numberOfChildren) return false;
  for (const /** @type {?} */ c in containee.children) {
    if (!container.children[c]) return false;
    if (!equalSegmentGroups(container.children[c], containee.children[c])) return false;
  }
  return true;
}
/**
 * @param {?} container
 * @param {?} containee
 * @return {?}
 */
function containsQueryParams(
    container: {[k: string]: string}, containee: {[k: string]: string}): boolean {
  return Object.keys(containee).length <= Object.keys(container).length &&
      Object.keys(containee).every(key => containee[key] === container[key]);
}
/**
 * @param {?} container
 * @param {?} containee
 * @return {?}
 */
function containsSegmentGroup(container: UrlSegmentGroup, containee: UrlSegmentGroup): boolean {
  return containsSegmentGroupHelper(container, containee, containee.segments);
}
/**
 * @param {?} container
 * @param {?} containee
 * @param {?} containeePaths
 * @return {?}
 */
function containsSegmentGroupHelper(
    container: UrlSegmentGroup, containee: UrlSegmentGroup, containeePaths: UrlSegment[]): boolean {
  if (container.segments.length > containeePaths.length) {
    const /** @type {?} */ current = container.segments.slice(0, containeePaths.length);
    if (!equalPath(current, containeePaths)) return false;
    if (containee.hasChildren()) return false;
    return true;

  } else if (container.segments.length === containeePaths.length) {
    if (!equalPath(container.segments, containeePaths)) return false;
    for (const /** @type {?} */ c in containee.children) {
      if (!container.children[c]) return false;
      if (!containsSegmentGroup(container.children[c], containee.children[c])) return false;
    }
    return true;

  } else {
    const /** @type {?} */ current = containeePaths.slice(0, container.segments.length);
    const /** @type {?} */ next = containeePaths.slice(container.segments.length);
    if (!equalPath(container.segments, current)) return false;
    if (!container.children[PRIMARY_OUTLET]) return false;
    return containsSegmentGroupHelper(container.children[PRIMARY_OUTLET], containee, next);
  }
}
/**
 * \@whatItDoes Represents the parsed URL.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({templateUrl:'template.html'}) 
 * class MyComponent {
 *   constructor(router: Router) {
 *     const tree: UrlTree =
 *       router.parseUrl('/team/33/(user/victor//support:help)?debug=true#fragment');
 *     const f = tree.fragment; // return 'fragment'
 *     const q = tree.queryParams; // returns {debug: 'true'}
 *     const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
 *     const s: UrlSegment[] = g.segments; // returns 2 segments 'team' and '33'
 *     g.children[PRIMARY_OUTLET].segments; // returns 2 segments 'user' and 'victor'
 *     g.children['support'].segments; // return 1 segment 'help'
 *   }
 * }
 * ```
 * 
 * \@description 
 * 
 * Since a router state is a tree, and the URL is nothing but a serialized state, the URL is a
 * serialized tree.
 * UrlTree is a data structure that provides a lot of affordances in dealing with URLs
 * 
 * \@stable
 */
export class UrlTree {
/**
 * \@internal
 */
_queryParamMap: ParamMap;
/**
 * \@internal
 * @param {?} root
 * @param {?} queryParams
 * @param {?} fragment
 */
constructor(
public root: UrlSegmentGroup,
public queryParams: {[key: string]: string},
public fragment: string|null) {}
/**
 * @return {?}
 */
get queryParamMap(): ParamMap {
    if (!this._queryParamMap) {
      this._queryParamMap = convertToParamMap(this.queryParams);
    }
    return this._queryParamMap;
  }
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return DEFAULT_SERIALIZER.serialize(this); }
}

function UrlTree_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
UrlTree.prototype._queryParamMap;
/**
 * The root segment group of the URL tree
 * @type {?}
 */
UrlTree.prototype.root;
/**
 * The query params of the URL
 * @type {?}
 */
UrlTree.prototype.queryParams;
/**
 * The fragment of the URL
 * @type {?}
 */
UrlTree.prototype.fragment;
}

/**
 * \@whatItDoes Represents the parsed URL segment group.
 * 
 * See {\@link UrlTree} for more information.
 * 
 * \@stable
 */
export class UrlSegmentGroup {
/**
 * \@internal
 */
_sourceSegment: UrlSegmentGroup;
/**
 * \@internal
 */
_segmentIndexShift: number;
/**
 * The parent node in the url tree
 */
parent: UrlSegmentGroup|null = null;
/**
 * @param {?} segments
 * @param {?} children
 */
constructor(
public segments: UrlSegment[],
public children: {[key: string]: UrlSegmentGroup}) {
    forEach(children, (v: any, k: any) => v.parent = this);
  }
/**
 * Whether the segment has child segments
 * @return {?}
 */
hasChildren(): boolean { return this.numberOfChildren > 0; }
/**
 * Number of child segments
 * @return {?}
 */
get numberOfChildren(): number { return Object.keys(this.children).length; }
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return serializePaths(this); }
}

function UrlSegmentGroup_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
UrlSegmentGroup.prototype._sourceSegment;
/**
 * \@internal
 * @type {?}
 */
UrlSegmentGroup.prototype._segmentIndexShift;
/**
 * The parent node in the url tree
 * @type {?}
 */
UrlSegmentGroup.prototype.parent;
/**
 * The URL segments of this group. See {\@link UrlSegment} for more information
 * @type {?}
 */
UrlSegmentGroup.prototype.segments;
/**
 * The list of children of this group
 * @type {?}
 */
UrlSegmentGroup.prototype.children;
}

/**
 * \@whatItDoes Represents a single URL segment.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({templateUrl:'template.html'}) 
 * class MyComponent {
 *   constructor(router: Router) {
 *     const tree: UrlTree = router.parseUrl('/team;id=33');
 *     const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
 *     const s: UrlSegment[] = g.segments;
 *     s[0].path; // returns 'team'
 *     s[0].parameters; // returns {id: 33}
 *   }
 * }
 * ```
 * 
 * \@description 
 * 
 * A UrlSegment is a part of a URL between the two slashes. It contains a path and the matrix
 * parameters associated with the segment.
 * 
 * \@stable
 */
export class UrlSegment {
/**
 * \@internal
 */
_parameterMap: ParamMap;
/**
 * @param {?} path
 * @param {?} parameters
 */
constructor(
public path: string,
public parameters: {[name: string]: string}) {}
/**
 * @return {?}
 */
get parameterMap() {
    if (!this._parameterMap) {
      this._parameterMap = convertToParamMap(this.parameters);
    }
    return this._parameterMap;
  }
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return serializePath(this); }
}

function UrlSegment_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
UrlSegment.prototype._parameterMap;
/**
 * The path part of a URL segment
 * @type {?}
 */
UrlSegment.prototype.path;
/**
 * The matrix parameters associated with a segment
 * @type {?}
 */
UrlSegment.prototype.parameters;
}

/**
 * @param {?} as
 * @param {?} bs
 * @return {?}
 */
export function equalSegments(as: UrlSegment[], bs: UrlSegment[]): boolean {
  return equalPath(as, bs) && as.every((a, i) => shallowEqual(a.parameters, bs[i].parameters));
}
/**
 * @param {?} as
 * @param {?} bs
 * @return {?}
 */
export function equalPath(as: UrlSegment[], bs: UrlSegment[]): boolean {
  if (as.length !== bs.length) return false;
  return as.every((a, i) => a.path === bs[i].path);
}
/**
 * @template T
 * @param {?} segment
 * @param {?} fn
 * @return {?}
 */
export function mapChildrenIntoArray<T>(
    segment: UrlSegmentGroup, fn: (v: UrlSegmentGroup, k: string) => T[]): T[] {
  let /** @type {?} */ res: T[] = [];
  forEach(segment.children, (child: UrlSegmentGroup, childOutlet: string) => {
    if (childOutlet === PRIMARY_OUTLET) {
      res = res.concat(fn(child, childOutlet));
    }
  });
  forEach(segment.children, (child: UrlSegmentGroup, childOutlet: string) => {
    if (childOutlet !== PRIMARY_OUTLET) {
      res = res.concat(fn(child, childOutlet));
    }
  });
  return res;
}
/**
 * \@whatItDoes Serializes and deserializes a URL string into a URL tree.
 * 
 * \@description The url serialization strategy is customizable. You can
 * make all URLs case insensitive by providing a custom UrlSerializer.
 * 
 * See {\@link DefaultUrlSerializer} for an example of a URL serializer.
 * 
 * \@stable
 * @abstract
 */
export abstract class UrlSerializer {
/**
 * Parse a url into a {\@link UrlTree}
 * @abstract
 * @param {?} url
 * @return {?}
 */
parse(url: string) {}
/**
 * Converts a {\@link UrlTree} into a url
 * @abstract
 * @param {?} tree
 * @return {?}
 */
serialize(tree: UrlTree) {}
}
/**
 * \@whatItDoes A default implementation of the {\@link UrlSerializer}.
 * 
 * \@description 
 * 
 * Example URLs:
 * 
 * ```
 * /inbox/33(popup:compose)
 * /inbox/33;open=true/messages/44
 * ```
 * 
 * DefaultUrlSerializer uses parentheses to serialize secondary segments (e.g., popup:compose), the
 * colon syntax to specify the outlet, and the ';parameter=value' syntax (e.g., open=true) to
 * specify route specific parameters.
 * 
 * \@stable
 */
export class DefaultUrlSerializer implements UrlSerializer {
/**
 * Parses a url into a {\@link UrlTree}
 * @param {?} url
 * @return {?}
 */
parse(url: string): UrlTree {
    const /** @type {?} */ p = new UrlParser(url);
    return new UrlTree(p.parseRootSegment(), p.parseQueryParams(), p.parseFragment());
  }
/**
 * Converts a {\@link UrlTree} into a url
 * @param {?} tree
 * @return {?}
 */
serialize(tree: UrlTree): string {
    const /** @type {?} */ segment = `/${serializeSegment(tree.root, true)}`;
    const /** @type {?} */ query = serializeQueryParams(tree.queryParams);
    const /** @type {?} */ fragment = typeof tree.fragment === `string` ? `#${encodeURI( /** @type {?} */((tree.fragment)))}` : '';

    return `${segment}${query}${fragment}`;
  }
}

const /** @type {?} */ DEFAULT_SERIALIZER = new DefaultUrlSerializer();
/**
 * @param {?} segment
 * @return {?}
 */
export function serializePaths(segment: UrlSegmentGroup): string {
  return segment.segments.map(p => serializePath(p)).join('/');
}
/**
 * @param {?} segment
 * @param {?} root
 * @return {?}
 */
function serializeSegment(segment: UrlSegmentGroup, root: boolean): string {
  if (!segment.hasChildren()) {
    return serializePaths(segment);
  }

  if (root) {
    const /** @type {?} */ primary = segment.children[PRIMARY_OUTLET] ?
        serializeSegment(segment.children[PRIMARY_OUTLET], false) :
        '';
    const /** @type {?} */ children: string[] = [];

    forEach(segment.children, (v: UrlSegmentGroup, k: string) => {
      if (k !== PRIMARY_OUTLET) {
        children.push(`${k}:${serializeSegment(v, false)}`);
      }
    });

    return children.length > 0 ? `${primary}(${children.join('//')})` : primary;

  } else {
    const /** @type {?} */ children = mapChildrenIntoArray(segment, (v: UrlSegmentGroup, k: string) => {
      if (k === PRIMARY_OUTLET) {
        return [serializeSegment(segment.children[PRIMARY_OUTLET], false)];
      }

      return [`${k}:${serializeSegment(v, false)}`];

    });

    return `${serializePaths(segment)}/(${children.join('//')})`;
  }
}
/**
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "\@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 * @param {?} s
 * @return {?}
 */
export function encode(s: string): string {
  return encodeURIComponent(s)
      .replace(/%40/g, '@')
      .replace(/%3A/gi, ':')
      .replace(/%24/g, '$')
      .replace(/%2C/gi, ',')
      .replace(/%3B/gi, ';');
}
/**
 * @param {?} s
 * @return {?}
 */
export function decode(s: string): string {
  return decodeURIComponent(s);
}
/**
 * @param {?} path
 * @return {?}
 */
export function serializePath(path: UrlSegment): string {
  return `${encode(path.path)}${serializeParams(path.parameters)}`;
}
/**
 * @param {?} params
 * @return {?}
 */
function serializeParams(params: {[key: string]: string}): string {
  return Object.keys(params).map(key => `;${encode(key)}=${encode(params[key])}`).join('');
}
/**
 * @param {?} params
 * @return {?}
 */
function serializeQueryParams(params: {[key: string]: any}): string {
  const /** @type {?} */ strParams: string[] = Object.keys(params).map((name) => {
    const /** @type {?} */ value = params[name];
    return Array.isArray(value) ? value.map(v => `${encode(name)}=${encode(v)}`).join('&') :
                                  `${encode(name)}=${encode(value)}`;
  });

  return strParams.length ? `?${strParams.join("&")}` : '';
}

const /** @type {?} */ SEGMENT_RE = /^[^\/()?;=&#]+/;
/**
 * @param {?} str
 * @return {?}
 */
function matchSegments(str: string): string {
  const /** @type {?} */ match = str.match(SEGMENT_RE);
  return match ? match[0] : '';
}

const /** @type {?} */ QUERY_PARAM_RE = /^[^=?&#]+/;
/**
 * @param {?} str
 * @return {?}
 */
function matchQueryParams(str: string): string {
  const /** @type {?} */ match = str.match(QUERY_PARAM_RE);
  return match ? match[0] : '';
}

const /** @type {?} */ QUERY_PARAM_VALUE_RE = /^[^?&#]+/;
/**
 * @param {?} str
 * @return {?}
 */
function matchUrlQueryParamValue(str: string): string {
  const /** @type {?} */ match = str.match(QUERY_PARAM_VALUE_RE);
  return match ? match[0] : '';
}
class UrlParser {
private remaining: string;
/**
 * @param {?} url
 */
constructor(private url: string) { this.remaining = url; }
/**
 * @return {?}
 */
parseRootSegment(): UrlSegmentGroup {
    this.consumeOptional('/');

    if (this.remaining === '' || this.peekStartsWith('?') || this.peekStartsWith('#')) {
      return new UrlSegmentGroup([], {});
    }

    // The root segment group never has segments
    return new UrlSegmentGroup([], this.parseChildren());
  }
/**
 * @return {?}
 */
parseQueryParams(): {[key: string]: any} {
    const /** @type {?} */ params: {[key: string]: any} = {};
    if (this.consumeOptional('?')) {
      do {
        this.parseQueryParam(params);
      } while (this.consumeOptional('&'));
    }
    return params;
  }
/**
 * @return {?}
 */
parseFragment(): string|null {
    return this.consumeOptional('#') ? decodeURI(this.remaining) : null;
  }
/**
 * @return {?}
 */
private parseChildren(): {[outlet: string]: UrlSegmentGroup} {
    if (this.remaining === '') {
      return {};
    }

    this.consumeOptional('/');

    const /** @type {?} */ segments: UrlSegment[] = [];
    if (!this.peekStartsWith('(')) {
      segments.push(this.parseSegment());
    }

    while (this.peekStartsWith('/') && !this.peekStartsWith('//') && !this.peekStartsWith('/(')) {
      this.capture('/');
      segments.push(this.parseSegment());
    }

    let /** @type {?} */ children: {[outlet: string]: UrlSegmentGroup} = {};
    if (this.peekStartsWith('/(')) {
      this.capture('/');
      children = this.parseParens(true);
    }

    let /** @type {?} */ res: {[outlet: string]: UrlSegmentGroup} = {};
    if (this.peekStartsWith('(')) {
      res = this.parseParens(false);
    }

    if (segments.length > 0 || Object.keys(children).length > 0) {
      res[PRIMARY_OUTLET] = new UrlSegmentGroup(segments, children);
    }

    return res;
  }
/**
 * @return {?}
 */
private parseSegment(): UrlSegment {
    const /** @type {?} */ path = matchSegments(this.remaining);
    if (path === '' && this.peekStartsWith(';')) {
      throw new Error(`Empty path url segment cannot have parameters: '${this.remaining}'.`);
    }

    this.capture(path);
    return new UrlSegment(decode(path), this.parseMatrixParams());
  }
/**
 * @return {?}
 */
private parseMatrixParams(): {[key: string]: any} {
    const /** @type {?} */ params: {[key: string]: any} = {};
    while (this.consumeOptional(';')) {
      this.parseParam(params);
    }
    return params;
  }
/**
 * @param {?} params
 * @return {?}
 */
private parseParam(params: {[key: string]: any}): void {
    const /** @type {?} */ key = matchSegments(this.remaining);
    if (!key) {
      return;
    }
    this.capture(key);
    let /** @type {?} */ value: any = '';
    if (this.consumeOptional('=')) {
      const /** @type {?} */ valueMatch = matchSegments(this.remaining);
      if (valueMatch) {
        value = valueMatch;
        this.capture(value);
      }
    }

    params[decode(key)] = decode(value);
  }
/**
 * @param {?} params
 * @return {?}
 */
private parseQueryParam(params: {[key: string]: any}): void {
    const /** @type {?} */ key = matchQueryParams(this.remaining);
    if (!key) {
      return;
    }
    this.capture(key);
    let /** @type {?} */ value: any = '';
    if (this.consumeOptional('=')) {
      const /** @type {?} */ valueMatch = matchUrlQueryParamValue(this.remaining);
      if (valueMatch) {
        value = valueMatch;
        this.capture(value);
      }
    }

    const /** @type {?} */ decodedKey = decode(key);
    const /** @type {?} */ decodedVal = decode(value);

    if (params.hasOwnProperty(decodedKey)) {
      // Append to existing values
      let /** @type {?} */ currentVal = params[decodedKey];
      if (!Array.isArray(currentVal)) {
        currentVal = [currentVal];
        params[decodedKey] = currentVal;
      }
      currentVal.push(decodedVal);
    } else {
      // Create a new value
      params[decodedKey] = decodedVal;
    }
  }
/**
 * @param {?} allowPrimary
 * @return {?}
 */
private parseParens(allowPrimary: boolean): {[outlet: string]: UrlSegmentGroup} {
    const /** @type {?} */ segments: {[key: string]: UrlSegmentGroup} = {};
    this.capture('(');

    while (!this.consumeOptional(')') && this.remaining.length > 0) {
      const /** @type {?} */ path = matchSegments(this.remaining);

      const /** @type {?} */ next = this.remaining[path.length];

      // if is is not one of these characters, then the segment was unescaped
      // or the group was not closed
      if (next !== '/' && next !== ')' && next !== ';') {
        throw new Error(`Cannot parse url '${this.url}'`);
      }

      let /** @type {?} */ outletName: string = /** @type {?} */(( undefined));
      if (path.indexOf(':') > -1) {
        outletName = path.substr(0, path.indexOf(':'));
        this.capture(outletName);
        this.capture(':');
      } else if (allowPrimary) {
        outletName = PRIMARY_OUTLET;
      }

      const /** @type {?} */ children = this.parseChildren();
      segments[outletName] = Object.keys(children).length === 1 ? children[PRIMARY_OUTLET] :
                                                                  new UrlSegmentGroup([], children);
      this.consumeOptional('//');
    }

    return segments;
  }
/**
 * @param {?} str
 * @return {?}
 */
private peekStartsWith(str: string): boolean { return this.remaining.startsWith(str); }
/**
 * @param {?} str
 * @return {?}
 */
private consumeOptional(str: string): boolean {
    if (this.peekStartsWith(str)) {
      this.remaining = this.remaining.substring(str.length);
      return true;
    }
    return false;
  }
/**
 * @param {?} str
 * @return {?}
 */
private capture(str: string): void {
    if (!this.consumeOptional(str)) {
      throw new Error(`Expected "${str}".`);
    }
  }
}

function UrlParser_tsickle_Closure_declarations() {
/** @type {?} */
UrlParser.prototype.remaining;
/** @type {?} */
UrlParser.prototype.url;
}

